import React from 'react';
import { cn, initials as initialsOf } from './model/derive';
import { KST, tzOffsetMinutes } from './model/time';

/**
 * 업무 가능 시간대 링 (1022 수정 1-1).
 *   24시간 = 원 1바퀴, 맨 위 = 00:00 KST, 시계 방향. 시간대 1개 = 호 1개 (자정을 넘으면 끊지 않고 이어서).
 *   각 시간대는 본인 시간대(tz) 기준 입력 → KST 로 바꿔 그린다.
 *   viewBox 48 · 반지름 22 · 두께 3 · round cap. 호 = green-deep02, 빈 시간(트랙) = green-deep03 8%.
 *   호 사이 간격 — 호 길이에서 7 을 빼고 앞뒤로 나눠 둔다 (round cap 을 포함해 눈에 보이는 틈).
 * 링은 바깥에 겹쳐 그리고(pointer-events 없음) 가운데 children(아바타)은 그대로 둔다.
 */

const R = 22;
const C = 2 * Math.PI * R;
const GAP = 7;
const DAY = 24 * 60;

const pad = (n) => String(n).padStart(2, '0');
const clock = (min) => `${pad(Math.floor(min / 60) % 24)}:${pad(min % 60)}`;
const minutesOf = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};

/** 본인 시간대 구간 → KST 분 [start, length] */
export const kstArcs = (slots = [], timezone = KST, at = new Date()) => {
  const shift = tzOffsetMinutes(at, KST) - tzOffsetMinutes(at, timezone || KST);
  return slots
    .filter((x) => x?.from && x?.to)
    .map((x) => {
      const start = (((minutesOf(x.from) + shift) % DAY) + DAY) % DAY;
      const length = (minutesOf(x.to) - minutesOf(x.from) + DAY) % DAY || DAY;
      return { start, length };
    })
    .sort((a, b) => a.start - b.start);
};

/** "Working hours · 08:00–13:00, 15:00–20:00 KST" */
export const ringLabel = (arcs) =>
  `Working hours · ${arcs.map((a) => `${clock(a.start)}–${clock((a.start + a.length) % DAY)}`).join(', ')} KST`;

/**
 * @param slots    [{ from, to }] (본인 시간대)
 * @param timezone 본인 시간대
 * @param size     링 바깥 지름(px)
 * @param inset    링이 아바타 바깥으로 나가는 폭(px) — 사이드바 5 (아바타 34 · 링 44)
 */
const WorkingHoursRing = ({
  slots,
  timezone,
  at,
  size,
  inset = 0,
  className,
  children,
  overlay,
}) => {
  const arcs = kstArcs(slots, timezone, at);
  const label = ringLabel(arcs);
  return (
    <span className={cn('relative inline-flex shrink-0', className)}>
      {children}
      <svg
        role='img'
        aria-label={label}
        viewBox='0 0 48 48'
        width={size}
        height={size}
        style={{ top: -inset, left: -inset }}
        className='pointer-events-none absolute -rotate-90 overflow-visible'
      >
        <title>{label}</title>
        <circle
          cx='24'
          cy='24'
          r={R}
          fill='none'
          strokeWidth='3'
          className='stroke-green-deep03/[0.08]'
        />
        {arcs.map((a) => {
          const len = (a.length / DAY) * C;
          // 하루 전체면 이어진 원, 아니면 앞뒤로 GAP/2 씩 비운다 (너무 짧으면 점 하나는 남긴다)
          const full = a.length >= DAY;
          const visible = full ? C : Math.max(len - GAP, 0.01);
          const offset = (a.start / DAY) * C + (full ? 0 : GAP / 2);
          return (
            <circle
              key={`${a.start}-${a.length}`}
              cx='24'
              cy='24'
              r={R}
              fill='none'
              strokeWidth='3'
              strokeLinecap='round'
              strokeDasharray={`${visible} ${C}`}
              strokeDashoffset={-offset}
              className='stroke-green-deep02 transition-[stroke-dasharray,stroke-dashoffset] duration-300 ease-out motion-reduce:transition-none'
            />
          );
        })}
      </svg>
      {/* 링 위에 올리는 것 (접속 점 등) */}
      {overlay}
    </span>
  );
};

/**
 * 링 아바타 — 사진(없으면 이니셜) + 업무 가능 시간대 링 (+ 접속 점).
 * 사이드바 프로필 영역(아바타 34 · 링 44) · Profile photo 카드(아바타 74 · 링 96)에서만 쓴다.
 */
export const RingAvatar = ({
  member,
  photo,
  slots,
  timezone,
  at,
  avatar,
  ring,
  textClass,
  dot,
  className,
}) => {
  const inset = (ring - avatar) / 2;
  return (
    <WorkingHoursRing
      slots={slots}
      timezone={timezone}
      at={at}
      size={ring}
      inset={inset}
      className={className}
      overlay={dot}
    >
      <span
        style={{ width: avatar, height: avatar }}
        className={cn(
          'flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-surface-green10 font-medium text-green-deep04',
          textClass,
        )}
      >
        {photo ? (
          <img src={photo} alt='' className='h-full w-full object-cover object-center' />
        ) : (
          initialsOf(member?.name)
        )}
      </span>
    </WorkingHoursRing>
  );
};

export default WorkingHoursRing;
